import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Loader2, Upload, X } from 'lucide-react';
import { useTrackFormSubmission } from '../../utils/analytics';

type FormData = {
  name: string;
  phone: string;
  email?: string;
  message?: string;
};

interface ContactFormProps {
  buttonText?: string;
  compact?: boolean;
  onSuccess?: () => void;
  labelClassName?: string;
}

const ContactForm: React.FC<ContactFormProps> = ({
  buttonText = 'Отправить заявку',
  compact = false,
  onSuccess,
  labelClassName,
}) => {
  const formRef = useRef<HTMLFormElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [phone, setPhone] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [formErrors, setFormErrors] = useState<Partial<Record<keyof FormData, string>>>({});
  const saveTimeoutRef = useRef<NodeJS.Timeout>();
  const formStartTime = useRef<number>(Date.now());
  const [formCompletion, setFormCompletion] = useState(0);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [networkError, setNetworkError] = useState(false);

  // Оптимизированное состояние формы
  const [formData, setFormData] = useState<Partial<FormData>>(() => {
    try {
      const saved = localStorage.getItem('contactFormData');
      const parsed = saved ? JSON.parse(saved) : {};
      // Проверяем, есть ли реальные данные
      const hasData = Object.values(parsed).some((value) => value && value.toString().trim());
      return hasData ? parsed : {};
    } catch {
      return {};
    }
  });

  // Оптимизированная функция сохранения с дебаунсом
  const saveFormData = useCallback((data: Partial<FormData>) => {
    // Проверяем, есть ли реальные данные для сохранения
    const hasData = Object.values(data).some((value) => value && value.toString().trim());
    if (!hasData) {
      setIsFormDirty(false);
      return;
    }

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    setIsSaving(true);
    saveTimeoutRef.current = setTimeout(() => {
      try {
        localStorage.setItem('contactFormData', JSON.stringify(data));
        setLastSaved(new Date());
        setNetworkError(false);
      } catch (error) {
        console.error('Failed to save form data:', error);
        setNetworkError(true);
      } finally {
        setIsSaving(false);
      }
    }, 1000);
  }, []);

  // Отслеживание прогресса заполнения формы
  const calculateFormCompletion = useCallback(
    (data: Partial<FormData>) => {
      let completion = 0;

      // Имя - 20%
      if (data.name?.trim()) {
        completion += 20;
      }

      // Телефон - 30% (50% - 20% от имени)
      if (data.phone?.trim() && data.phone.length >= 18) {
        completion += 30;
      }

      // Email - 20% (70% - 50%)
      if (data.email?.trim()) {
        completion += 20;
      }

      // Сообщение - 20% (90% - 70%)
      if (data.message?.trim()) {
        completion += 20;
      }

      // Файл - 10% (100% - 90%)
      if (selectedFile) {
        completion += 10;
      }

      return completion;
    },
    [selectedFile]
  );

  // Восстановление формы после сбоя
  const handleRecovery = useCallback(() => {
    const savedData = localStorage.getItem('contactFormData');
    if (savedData) {
      try {
        const parsedData = JSON.parse(savedData);
        setFormData(parsedData);
        setFormCompletion(calculateFormCompletion(parsedData));
        setIsFormDirty(true);
        setNetworkError(false);
      } catch (error) {
        console.error('Failed to recover form data:', error);
      }
    }
  }, [calculateFormCompletion]);

  // Оптимизированное обновление прогресса
  const updateFormProgress = useCallback(
    (data: Partial<FormData>) => {
      const newCompletion = calculateFormCompletion(data);
      setFormCompletion(newCompletion);
    },
    [calculateFormCompletion]
  );

  // Оптимизированный обработчик изменений
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value } = e.target;

      setFormData((prev) => {
        const newData = { ...prev, [name]: value };
        const hasData = Object.values(newData).some((val) => val && val.toString().trim());
        setIsFormDirty(hasData);
        updateFormProgress(newData);
        if (hasData) {
          saveFormData(newData);
        }
        return newData;
      });
    },
    [updateFormProgress, saveFormData]
  );

  // Загружаем сохраненные данные при монтировании
  useEffect(() => {
    const savedData = localStorage.getItem('contactFormData');
    if (savedData) {
      try {
        const parsedData = JSON.parse(savedData);
        setFormData(parsedData);
        setFormCompletion(calculateFormCompletion(parsedData));
        setIsFormDirty(true);
      } catch (error) {
        console.error('Failed to load saved form data:', error);
      }
    }
  }, [calculateFormCompletion]);

  // Оптимизированная функция форматирования телефона
  const formatPhoneNumber = useMemo(
    () => (value: string) => {
      const numbers = value.replace(/\D/g, '');
      if (numbers.length === 0) return '';
      if (numbers.length <= 1) return `+7 (${numbers}`;
      if (numbers.length <= 4) return `+7 (${numbers.slice(1, 4)}`;
      if (numbers.length <= 7) return `+7 (${numbers.slice(1, 4)}) ${numbers.slice(4, 7)}`;
      if (numbers.length <= 9)
        return `+7 (${numbers.slice(1, 4)}) ${numbers.slice(4, 7)}-${numbers.slice(7, 9)}`;
      return `+7 (${numbers.slice(1, 4)}) ${numbers.slice(4, 7)}-${numbers.slice(7, 9)}-${numbers.slice(9, 11)}`;
    },
    []
  );

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formattedPhone = formatPhoneNumber(e.target.value);
    setPhone(formattedPhone);
    // Очищаем ошибку при вводе
    if (formErrors.phone) {
      setFormErrors((prev) => ({ ...prev, phone: undefined }));
    }
    // Обновляем прогресс
    setFormData((prev) => {
      const newData = { ...prev, phone: formattedPhone };
      const newCompletion = calculateFormCompletion(newData);
      setFormCompletion(newCompletion);
      saveFormData(newData);
      return newData;
    });
  };

  // Оптимизированная обработка файлов
  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files[0]) {
        const file = e.target.files[0];

        // Проверка размера файла (максимум 10MB)
        if (file.size > 10 * 1024 * 1024) {
          setError('Размер файла не должен превышать 10MB');
          return;
        }

        // Базовая проверка на вредоносные файлы
        if (file.name.includes('..') || file.name.includes('/') || file.name.includes('\\')) {
          setError('Недопустимое имя файла');
          return;
        }

        setSelectedFile(file);
        setError('');
        setIsFormDirty(true);
        updateFormProgress({ ...formData });
      }
    },
    [formData, updateFormProgress]
  );

  const handleFileRemove = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    setError('');
    setIsFormDirty(true);
    updateFormProgress({ ...formData });
  };

  const trackSubmit = useTrackFormSubmission('contact_form', {
    timeSpent: Math.round((Date.now() - formStartTime.current) / 1000),
    completionPercentage: formCompletion,
    fieldsFilled: Object.keys(formData).filter(
      (key) => formData[key as keyof FormData]
    ) as string[],
    isComplete: formCompletion === 100,
  });

  const validateForm = useCallback((): string | null => {
    if (!formData.name || !formData.name.trim()) return 'Укажите имя';
    if (!phone.trim()) return 'Укажите телефон';
    if (!/^\+7 \(\d{3}\) \d{3}-\d{2}-\d{2}$/.test(phone)) {
      return 'Введите телефон в формате +7 (999) 999-99-99';
    }
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      return 'Введите корректный email адрес';
    }

    return null;
  }, [formData.name, phone, formData.email]);

  const fileToBase64 = useCallback((file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result;
        if (typeof result === 'string') {
          resolve(result.split(',')[1] || '');
          return;
        }
        reject(new Error('Failed to read file'));
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  }, []);

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      setFormErrors({
        name: !formData.name?.trim() ? 'Укажите имя' : undefined,
        phone: !phone.trim()
          ? 'Укажите телефон'
          : !/^\+7 \(\d{3}\) \d{3}-\d{2}-\d{2}$/.test(phone)
            ? 'Введите телефон в формате +7 (999) 999-99-99'
            : undefined,
        email:
          formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)
            ? 'Введите корректный email адрес'
            : undefined,
      });
      setSuccess(false);
      return;
    }

    setFormErrors({});

    const email = formData.email?.trim() || 'Не указан';
    const message = formData.message?.trim() || 'Нет сообщения';
    const fileData = selectedFile
      ? {
          name: selectedFile.name,
          content: await fileToBase64(selectedFile),
        }
      : null;

    setLoading(true);
    setError('');

    const bodyObject = {
      name: formData.name?.trim() ?? '',
      email,
      phone: phone.trim(),
      message,
      fileData,
    };

    console.log('Sending to URL:', import.meta.env.VITE_CLOUD_FUNCTION_URL);
    console.log('Sending body:', JSON.stringify(bodyObject));

    try {
      const response = await fetch(import.meta.env.VITE_CLOUD_FUNCTION_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bodyObject),
      });
      // Если это preflight-запрос (OPTIONS), он может завершиться успешно,
      // но нам нужно дождаться реального POST-запроса.
      // Если fetch вернул ошибку, обрабатываем её.
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Server error response:', errorText);
        throw new Error(`Ошибка сервера: ${response.status}`);
      }

      trackSubmit(e);

      setSuccess(true);
      if (onSuccess) {
        onSuccess();
      }

      setFormData({ name: '', phone: '', email: '', message: '' });
      setPhone('');
      setSelectedFile(null);
      setFormErrors({});
      setIsFormDirty(false);
      setFormCompletion(0);
      localStorage.removeItem('contactFormData');
    } catch (err: unknown) {
      console.error('Failed to send form:', err);
      setError(err instanceof Error ? err.message : 'Произошла ошибка при отправке формы');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="text-center py-8 animate-fade-in" role="alert">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 text-green-600 mb-4">
          <svg
            className="w-8 h-8"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h3 className="text-2xl font-bold text-gray-900 mb-2">Оставьте заявку</h3>
        <p className="text-gray-600">
          Заполните форму, и наш менеджер свяжется с вами в течение 2 часов (в рабочее время).
        </p>
      </div>
    );
  }

  return (
    <form
      ref={formRef}
      onSubmit={onSubmit}
      className="space-y-4"
      aria-label="Контактная форма"
      noValidate
    >
      {isFormDirty && (
        <>
          <div className="flex items-center justify-between text-xs text-gray-500 mb-2">
            <div className="flex items-center space-x-2">
              {isSaving ? (
                <>
                  <Loader2 className="h-3 w-3 animate-spin" />
                  <span>Сохранение...</span>
                </>
              ) : lastSaved ? (
                <>
                  <svg
                    className="h-3 w-3 text-success-500"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  <span>Сохранено {lastSaved.toLocaleTimeString()}</span>
                </>
              ) : null}
            </div>
            {networkError && (
              <button
                type="button"
                onClick={handleRecovery}
                className="text-error-500 hover:text-error-600 text-xs underline"
              >
                Восстановить данные
              </button>
            )}
          </div>

          {formCompletion > 0 && (
            <div className="mb-4">
              <div className="flex justify-between text-sm text-gray-600 mb-1">
                <span>Прогресс заполнения</span>
                <span className="font-medium">{formCompletion}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
                <div
                  className="bg-primary-600 h-2.5 rounded-full transition-all duration-500 ease-out"
                  style={{
                    width: `${formCompletion}%`,
                    transform: 'translateX(0)',
                    boxShadow: '0 0 8px rgba(59, 130, 246, 0.5)',
                  }}
                />
              </div>
              <div className="mt-1 text-xs text-gray-500">
                {formCompletion < 20 && 'Начните с ввода имени'}
                {formCompletion >= 20 &&
                  formCompletion < 50 &&
                  !formData.phone?.trim() &&
                  'Добавьте номер телефона'}
                {formCompletion >= 20 &&
                  formCompletion < 50 &&
                  formData.phone?.trim() &&
                  formData.phone.length < 18 &&
                  'Завершите ввод номера телефона'}
                {formCompletion >= 50 &&
                  formCompletion < 70 &&
                  !formData.email?.trim() &&
                  'Укажите email для связи'}
                {formCompletion >= 50 &&
                  formCompletion < 70 &&
                  formData.email?.trim() &&
                  'Добавьте описание запроса'}
                {formCompletion >= 70 && formCompletion < 90 && 'Опишите ваш запрос подробнее'}
                {formCompletion >= 90 &&
                  formCompletion < 100 &&
                  'При необходимости прикрепите файл'}
                {formCompletion === 100 && 'Форма заполнена полностью'}
              </div>
            </div>
          )}
        </>
      )}

      <div>
        <label
          htmlFor="name"
          className={`block text-sm font-medium text-gray-700 mb-1 ${labelClassName}`}
        >
          Имя*
        </label>
        <input
          type="text"
          id="name"
          name="name"
          required
          value={formData.name || ''}
          onChange={handleInputChange}
          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500 transition-colors"
          placeholder="Введите Ваше имя"
          aria-required="true"
          aria-invalid={!!formErrors.name}
          aria-describedby={formErrors.name ? 'name-error' : undefined}
        />
        {formErrors.name && (
          <p id="name-error" className="mt-1 text-sm text-red-600 animate-shake">
            {formErrors.name}
          </p>
        )}
      </div>

      <div>
        <label
          htmlFor="phone"
          className={`block text-sm font-medium text-gray-700 mb-1 ${labelClassName}`}
        >
          Телефон*
        </label>
        <input
          type="tel"
          id="phone"
          name="phone"
          required
          value={phone}
          onChange={handlePhoneChange}
          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500 transition-colors"
          placeholder="+7 (___) ___-__-__"
          aria-required="true"
          aria-invalid={!!formErrors.phone}
          aria-describedby={formErrors.phone ? 'phone-error' : undefined}
        />
        {formErrors.phone && (
          <p id="phone-error" className="mt-1 text-sm text-red-600 animate-shake">
            {formErrors.phone}
          </p>
        )}
      </div>

      <div>
        <label
          htmlFor="email"
          className={`block text-sm font-medium text-gray-700 mb-1 ${labelClassName}`}
        >
          E-mail
        </label>
        <input
          type="email"
          id="email"
          name="email"
          value={formData.email || ''}
          onChange={handleInputChange}
          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500 transition-colors"
          placeholder="example@company.com"
          aria-invalid={!!formErrors.email}
          aria-describedby={formErrors.email ? 'email-error' : undefined}
        />
        {formErrors.email && (
          <p id="email-error" className="mt-1 text-sm text-red-600 animate-shake">
            {formErrors.email}
          </p>
        )}
      </div>

      {!compact && (
        <div>
          <label
            htmlFor="message"
            className={`block text-sm font-medium text-gray-700 mb-1 ${labelClassName}`}
          >
            Чем можем помочь?
          </label>
          <textarea
            id="message"
            name="message"
            rows={1}
            value={formData.message || ''}
            onChange={handleInputChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500 resize-y transition-colors"
            placeholder="Опишите Ваш запрос или ТЗ"
            aria-invalid={!!formErrors.message}
            aria-describedby={formErrors.message ? 'message-error' : undefined}
          />
          {formErrors.message && (
            <p id="message-error" className="mt-1 text-sm text-red-600 animate-shake">
              {formErrors.message}
            </p>
          )}
        </div>
      )}

      <div className="mt-1 flex items-center">
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          className="hidden"
          accept="*/*"
          aria-label="Прикрепить файл"
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-all duration-200"
          aria-label="Добавить файл"
        >
          <Upload className="h-5 w-5 mr-2 text-gray-400" aria-hidden="true" />
          Добавьте файл - ТЗ, Опросный лист, или КП конкурентов
        </button>
      </div>
      {selectedFile && (
        <div className="mt-2 flex items-center space-x-2 text-sm text-gray-600 animate-fade-in">
          <span>{selectedFile.name}</span>
          <button
            type="button"
            onClick={handleFileRemove}
            className="text-gray-400 hover:text-gray-500 transition-colors"
            aria-label="Удалить файл"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      )}
      <p className="mt-1 text-sm text-gray-500">Максимальный размер файла: 10MB</p>

      {error && (
        <div
          className="text-error-600 text-sm animate-shake p-3 bg-error-50 rounded-md border border-error-200"
          role="alert"
        >
          <div className="flex items-start">
            <svg
              className="h-5 w-5 text-error-500 mr-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <div>
              <p className="font-medium">Ошибка</p>
              <p className="mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}

      <div className="pt-2">
        <button
          type="submit"
          disabled={loading || isSaving}
          className="w-full btn-primary disabled:opacity-70 disabled:cursor-not-allowed transition-all duration-200 hover:shadow-lg"
          aria-busy={loading}
        >
          {loading ? (
            <span className="flex items-center justify-center">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
              Отправка...
            </span>
          ) : isSaving ? (
            <span className="flex items-center justify-center">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
              Сохранение...
            </span>
          ) : (
            buttonText
          )}
        </button>
      </div>
    </form>
  );
};

export default ContactForm;
